"use client";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { setAnalyticsConsent, arrivedViaTrackedLink } from "@/lib/attribution";
import { setConsentDecision, consentUndecided } from "@/lib/consent";

/**
 * Minimal, non-blocking analytics-consent banner.
 *
 * Shows only when BOTH are true:
 *   - the visitor has not yet made a consent decision, AND
 *   - they arrived via a tracked link (UTM / fbclid / gclid / campaign_id).
 *
 * Until the visitor accepts, attribution capture stays session-only (in
 * memory) per the S3 consent model. Accepting promotes the captured touches
 * to localStorage; declining keeps them session-scoped. Either choice
 * dismisses the banner for good.
 */
export function AnalyticsConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Decide visibility once, at mount. Capture already ran at bootstrap.
    if (consentUndecided() && arrivedViaTrackedLink()) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const accept = () => {
    setConsentDecision("granted");
    setAnalyticsConsent(true); // flushes in-memory touches to storage
    setVisible(false);
  };

  const decline = () => {
    setConsentDecision("denied");
    setAnalyticsConsent(false); // keep session-only
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label={t("v2.consent.aria_label", "Analytics consent")}
      className="pointer-events-auto fixed bottom-0 left-1/2 z-50 w-full max-w-[420px] -translate-x-1/2 px-3 pb-3"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div
        className="rounded-2xl bg-white shadow-[0_10px_40px_-12px_rgba(0,0,0,0.25)] ring-1 ring-black/5"
        style={{ borderTop: "3px solid var(--primary-500, #0baa57)" }}
      >
        <div className="p-3">
          <div className="text-sm font-semibold text-gray-900">
            {t("v2.consent.title", "Help us improve")}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {t(
              "v2.consent.body",
              "We'd like to remember how you found us to make this menu better. No personal details are stored.",
            )}
          </div>
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={decline}
              className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 active:bg-gray-200"
            >
              {t("v2.consent.decline", "No thanks")}
            </button>
            <button
              type="button"
              onClick={accept}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 active:opacity-80"
              style={{ background: "var(--primary-500, #0baa57)" }}
            >
              {t("v2.consent.accept", "Sure")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
